import React, { useCallback, useEffect, useRef } from "react"
import { GoogleMap, Marker, OverlayView } from "@react-google-maps/api"
import { Box, Typography } from "@material-ui/core"
import RoomIcon from "@material-ui/icons/Room"
import type { MapaRoteirizacaoBaseProps, MarcadorPedido } from "./tipos"
import { CENTRO_PADRAO, dicaMarcador, estilosEtiqueta, iniciaisEntregador } from "./tipos"
import { CASA_SVG_DATA_URL, CASA_TAMANHO } from "./icones"
import { agruparPorEndereco, grupoStyle } from "./estiloPin"

const containerStyle = { width: "100%", height: "100%" }

const opcoesMapa = {
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    clickableIcons: false,
}

/** Etiqueta colorida (por status) com o número do pedido, apontando o endereço. */
function EtiquetaPedido({ m, onSelecionar }: { m: MarcadorPedido; onSelecionar: (id: number) => void }) {
    const estilos = estilosEtiqueta(m)
    const iniciais = iniciaisEntregador(m.nomeEntregador)
    return (
        <div
            onClick={e => {
                e.stopPropagation()
                onSelecionar(m.id)
            }}
            title={dicaMarcador(m)}
            style={estilos.container as React.CSSProperties}
        >
            <div style={estilos.caixa as React.CSSProperties}>
                <span style={estilos.numero as React.CSSProperties}>{m.shortReference}</span>
                {iniciais && <span style={estilos.iniciais as React.CSSProperties}>{iniciais}</span>}
            </div>
            <div style={estilos.seta as React.CSSProperties} />
        </div>
    )
}

export function GoogleMapaRoteirizacao({
    origem,
    marcadores,
    onSelecionarPedido,
}: MapaRoteirizacaoBaseProps) {
    const mapRef = useRef<any>(null)
    const idsKey = marcadores.map(m => m.id).join(",")

    const ajustarLimites = useCallback(
        (map: any) => {
            const google = (window as any).google
            if (!google || !google.maps) return
            const pontos = marcadores.map(m => ({ lat: m.latitude, lng: m.longitude }))
            if (origem) pontos.push(origem)
            if (pontos.length === 0) return
            if (pontos.length === 1) {
                map.setCenter(pontos[0])
                map.setZoom(15)
                return
            }
            const bounds = new google.maps.LatLngBounds()
            pontos.forEach(p => bounds.extend(p))
            map.fitBounds(bounds)
        },
        [marcadores, origem]
    )

    const onLoad = useCallback(
        (map: any) => {
            mapRef.current = map
            ajustarLimites(map)
        },
        [ajustarLimites]
    )

    // Reenquadra quando o conjunto de pedidos muda (não a cada seleção).
    useEffect(() => {
        if (mapRef.current) ajustarLimites(mapRef.current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey])

    const google = (window as any).google
    if (!google || !google.maps) {
        return (
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                p={3}
                style={{ textAlign: "center", color: "#7f8c8d" }}
            >
                <RoomIcon style={{ fontSize: 48, marginBottom: 8 }} />
                <Typography variant="body1">
                    Mapa indisponível: o token do Google Maps não está configurado para este
                    estabelecimento.
                </Typography>
            </Box>
        )
    }

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={origem ?? CENTRO_PADRAO}
            zoom={origem ? 13 : 4}
            onLoad={onLoad}
            options={opcoesMapa}
        >
            {origem && (
                <Marker
                    position={origem}
                    title="Restaurante"
                    icon={{
                        url: CASA_SVG_DATA_URL,
                        scaledSize: new google.maps.Size(CASA_TAMANHO, CASA_TAMANHO),
                        anchor: new google.maps.Point(CASA_TAMANHO / 2, CASA_TAMANHO / 2),
                    }}
                    zIndex={999}
                />
            )}

            {agruparPorEndereco(marcadores).map(grupo => (
                <OverlayView
                    key={grupo.map(m => m.id).join("-")}
                    position={{ lat: grupo[0].latitude, lng: grupo[0].longitude }}
                    mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    getPixelPositionOffset={(width, height) => ({
                        x: -(width / 2),
                        y: -height,
                    })}
                >
                    <div style={grupoStyle}>
                        {grupo.map(m => (
                            <EtiquetaPedido key={m.id} m={m} onSelecionar={onSelecionarPedido} />
                        ))}
                    </div>
                </OverlayView>
            ))}
        </GoogleMap>
    )
}
